/**
 * Parse a column id or column-state entry and extract TSG info.
 * Pattern: '<TSG_KEY>__<subField>[.<rest>...]'
 * @param {string|object} input - id string or column state entry (colId/field)
 * @returns {{isTsg: boolean, tsgKey?: string, subField?: string}}
 *
 * Prefer attaching metadata to `colDef.meta` if possible to avoid parsing.
 */
export const parseTsgColumnId = input => {
  if (!input) return { isTsg: false };
  const id = typeof input === 'string' ? input : input.colId || input.field || '';
  if (!id || typeof id !== 'string') return { isTsg: false };

  // Match patterns like: PR__linePricingGroupKey or PR__distributionByAssortmentRole.role
  const m = id.match(/^([^_]+)__([^.]+)(?:\..*)?$/);
  if (!m) return { isTsg: false };
  const [, tsgKey, subField] = m;
  return { isTsg: true, tsgKey, subField };
};

/**
 * Extract selected TSG keys from an attribute filter structure.
 * Normalises values to strings, ignores invalid entries and deduplicates.
 * @param {Array|null|undefined} attributeFilter - filter payload from attribute-panel
 * @returns {string[]|null} - array of selected TSG keys or null when none
 */
export const extractSelectedTsgKeysFromFilter = attributeFilter => {
  if (!attributeFilter || !Array.isArray(attributeFilter)) return null;
  const rule = attributeFilter.find(r => r.attributeKey === 'toolStoreGroupKey');
  if (!rule || !Array.isArray(rule.attributeValue)) return null;

  const normalized = rule.attributeValue
    .map(v => {
      if (typeof v === 'string') return v;
      if (v && typeof v === 'object') {
        return v.attributeValue || v.value || v.attributeKey || v.id || null;
      }
      return null;
    })
    .filter(Boolean);

  // Deduplicate while preserving order
  const seen = new Set();
  const unique = [];
  normalized.forEach(s => {
    if (!seen.has(s)) {
      seen.add(s);
      unique.push(s);
    }
  });

  return unique.length ? unique : null;
};

/**
 * Compute a presentation-only transformed column state.
 * Pure function: does not touch grid API or Vuex.
 * @param {Array} rawColumnState - array from gridApi.getColumnState() or persisted state
 * @param {Array} attributeFilter - current attribute filter payload
 * @param {boolean} isChainsModeEnabled - whether chains mode is active
 * @param {Array<string>} tsgSubcolumnsShownWhenFiltering - subcolumns to show when Price List filtering is applied
 * @param {Object<string, {tsgKey:string,subField:string}>} colMetaMap - optional map of colId -> meta provided by column defs
 * @returns {Array} transformed column state suitable for applyColumnState
 */
export const buildHideShowColumnState = (
  rawColumnState,
  attributeFilter,
  isChainsModeEnabled,
  tsgSubcolumnsShownWhenFiltering = ['linePricingGroupKey', 'linePriceFactor'],
  colMetaMap = null
) => {
  if (!Array.isArray(rawColumnState)) return rawColumnState;

  const selected = extractSelectedTsgKeysFromFilter(attributeFilter) || [];
  const filterActive = isChainsModeEnabled && selected.length > 0;

  return rawColumnState.map(entry => {
    const id = entry.colId || entry.field || '';
    // Prefer explicit metadata when available (from colDef.meta), otherwise fall back to parsing
    const meta = colMetaMap && colMetaMap[id] ? colMetaMap[id] : null;
    const parsed = meta
      ? { isTsg: true, tsgKey: meta.tsgKey, subField: meta.subField }
      : parseTsgColumnId(id);
    if (!parsed.isTsg) return entry;
    if (filterActive && !selected.includes(parsed.tsgKey)) return { ...entry, hide: true };
    return { ...entry, hide: !tsgSubcolumnsShownWhenFiltering.includes(parsed.subField) };
  });
};

/**
 * Build a map of colId -> meta from grid column objects (pure, no ag-Grid API calls)
 * @param {Array} gridColumns - items returned by columnApi.getAllGridColumns()
 * @returns {Object} map of colId -> meta
 */
export const extractColMetaMapFromColumns = gridColumns => {
  if (!Array.isArray(gridColumns)) return null;
  return gridColumns.reduce((m, c) => {
    try {
      const def = c && typeof c.getColDef === 'function' ? c.getColDef() : null;
      const id = c && typeof c.getColId === 'function' ? c.getColId() : null;
      if (def && def.meta && id) m[id] = def.meta;
    } catch (e) {
      // ignore malformed entries
    }
    return m;
  }, {});
};

/**
 * Handle fill operation for line pricing group columns with subGroup matching and scaling factor logic.
 * Only copies values to rows with matching subGroups when sourceSubGroups is provided.
 * @param {Object} params - AG-Grid fillOperation parameters
 * @param {Array<string>} sourceSubGroups - SubGroup values from the selected range (optional)
 * @returns {Object} - { shouldFill: boolean, fillValue: any, scalingFactorUpdate: Object, tsgKey: string }
 */
export const handleLinePricingGroupFillOperation = (params, sourceSubGroups) => {
  const { field, meta } = params.column.colDef;

  const isLinePricingGroupColumn =
    (meta && meta.subField === 'linePricingGroupKey') ||
    field === 'linePricingGroupKey' ||
    (field && field.includes('__linePricingGroupKey'));

  if (!isLinePricingGroupColumn) {
    return { shouldFill: false };
  }

  const currentNode = params.rowNode;
  const currentSubGroup = currentNode && currentNode.data ? currentNode.data.subGroup : null;

  let fillValue;

  if (sourceSubGroups && sourceSubGroups.length > 0) {
    const matchingSourceIndex = sourceSubGroups.findIndex(sg => sg === currentSubGroup);

    // Special case: if first value is empty, allow cross-subGroup clearing
    const firstValue = params.initialValues[0];
    const isEmptyFirstValue = firstValue === null || firstValue === undefined || firstValue === '';

    if (matchingSourceIndex === -1 && !isEmptyFirstValue) {
      return { shouldFill: true, fillValue: params.currentCellValue };
    }

    fillValue = params.initialValues[matchingSourceIndex];
  } else {
    fillValue = params.initialValues[params.currentIndex % params.initialValues.length];
  }

  const availableValues = params.column.colDef.cellEditorParams({
    data: params.rowNode.data,
    node: params.rowNode,
  });

  const isEmptyValue = fillValue === null || fillValue === undefined || fillValue === '';
  if (!isEmptyValue && !availableValues.values.includes(fillValue)) {
    return { shouldFill: true, fillValue: params.currentCellValue };
  }

  const tsgKey = meta && meta.tsgKey ? meta.tsgKey : null;
  const linePriceFactorField = tsgKey ? `${tsgKey}__linePriceFactor` : 'linePriceFactor';

  const currentRow = params.rowNode.data;
  let scalingFactorUpdate = null;
  if (fillValue === null || fillValue === undefined || fillValue === '') {
    scalingFactorUpdate = { field: linePriceFactorField, value: '' };
  } else if (
    currentRow[linePriceFactorField] === null ||
    currentRow[linePriceFactorField] === undefined ||
    currentRow[linePriceFactorField] === ''
  ) {
    scalingFactorUpdate = { field: linePriceFactorField, value: 1 };
  }

  return {
    shouldFill: true,
    fillValue,
    scalingFactorUpdate,
    tsgKey,
  };
};

/**
 * Determines if scaling factor should be cleared when line pricing group is cleared
 * @param {Object} params - Cell value change parameters from AG-Grid
 * @returns {Object|null} { linePriceFactorField: string } or null if no update needed
 */
export const handleLinePricingGroupValueChange = params => {
  const { field, meta } = params.colDef;

  const isLinePricingGroupColumn =
    (meta && meta.subField === 'linePricingGroupKey') ||
    field === 'linePricingGroupKey' ||
    (field && field.includes('__linePricingGroupKey'));

  if (!isLinePricingGroupColumn) {
    return null;
  }

  const newValue = params.newValue;
  const isEmptyValue = newValue === null || newValue === undefined || newValue === '';

  const tsgKey = meta && meta.tsgKey ? meta.tsgKey : null;
  const linePriceFactorField = tsgKey ? `${tsgKey}__linePriceFactor` : 'linePriceFactor';

  if (isEmptyValue) {
    // Clearing group: clear the factor
    return { linePriceFactorField, nextFactorValue: '' };
  }

  // Assigning group: if factor is missing, set default to 1; else keep as-is
  const row = (params && params.data) || {};
  const current = row[linePriceFactorField];
  const isMissingFactor =
    current === null || current === undefined || current === '' || Number.isNaN(current);

  if (isMissingFactor) {
    return { linePriceFactorField, nextFactorValue: 1 };
  }

  return null;
};
